import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  REQUEST_TYPE_MAP,
  downloadMedicalAttendance,
  fetchHospitals,
  findOneMedicalAttendance,
  formatSapDate,
  getStatusBadgeClass,
  getStatusLabel,
} from "./medicalAttendance.service";

const NO_DATA = "-";

const ViewMedicalAttendance: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hospitalLabel, setHospitalLabel] = useState("");
  const [downloading, setDownloading] = useState(false);

  const refId = referenceNumber ? decodeURIComponent(referenceNumber) : "";

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneMedicalAttendance(refId);
        setDetail(data);
        const key = data?.HospitalRecordKey;
        const requestType = data?.RequestTypeForMedicalAttendanceRules;
        const requestTypeStr = requestType != null ? String(requestType) : "";

        if (requestTypeStr === "62") {
          const name = data?.HospitalName ? String(data.HospitalName).trim() : "";
          const address = data?.HospitalAddress ? String(data.HospitalAddress).trim() : "";
          if (name && address) setHospitalLabel(`${name} - ${address}`);
          else setHospitalLabel(name || address || (key != null ? String(key) : ""));
        } else if (key && requestType) {
          try {
            const options = await fetchHospitals(String(requestType));
            const opt = options.find((d) => String(d.id) === String(key));
            setHospitalLabel(opt?.label || String(key));
          } catch {
            setHospitalLabel(String(key));
          }
        } else {
          setHospitalLabel(data?.HospitalRecordKey != null ? String(data.HospitalRecordKey) : "");
        }
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refId, t]);

  const isApproved =
    String(detail?.RequestStatus ?? "").trim().toUpperCase() === "A" ||
    String(detail?.RequestStatus ?? "").trim().toUpperCase() === "APPROVED";

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const url = await downloadMedicalAttendance(detail?.ReferenceNumber || refId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      toast.error(err?.message || Api.getErrorMessage(err) || "Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const requestTypeRaw =
    detail?.RequestTypeForMedicalAttendanceRules != null
      ? String(detail.RequestTypeForMedicalAttendanceRules)
      : "";
  const requestTypeLabel = requestTypeRaw ? REQUEST_TYPE_MAP[requestTypeRaw] ?? requestTypeRaw : "";
  const createdDate = detail?.created_at ? formatSapDate(detail.created_at).replace(/-/g, "/") : "";
  const displayHospital =
    hospitalLabel ||
    (detail?.HospitalRecordKey != null ? String(detail.HospitalRecordKey) : "") ||
    "-";
  const displayDisease = detail?.DiseaseName || "";
  const displayHospitalName =
    !displayHospital || displayHospital === "-"
      ? "-"
      : displayHospital.includes(" - ")
        ? displayHospital.split(" - ")[0].trim() || displayHospital
        : displayHospital;
  const displayHospitalAddress =
    hospitalLabel && hospitalLabel.includes(" - ")
      ? hospitalLabel.split(" - ").slice(1).join(" - ").trim() || t("no_data_found") || NO_DATA
      : t("no_data_found") || NO_DATA;

  const toRemarks = Array.isArray(detail?.ToRemarks?.results)
    ? detail.ToRemarks.results
    : Array.isArray(detail?.ToRemarks)
      ? detail.ToRemarks
      : Array.isArray(detail?.RemarksHistory)
        ? detail.RemarksHistory
        : [];
  const lastApprover =
    detail?.LatestApproverName?.trim() ||
    (toRemarks.length > 0 ? toRemarks[0]?.ApproverName ?? "N/A" : "N/A");

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("ivf_request_details")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.medicalAttendance.list}>{t("prior_approval_for_ivf_para_6_2_6_3_treatment")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("view_details")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            {isApproved && detail && (
              <button className="btn btn-danger" onClick={handleDownload} disabled={downloading}>
                {downloading ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="ti ti-download me-1"></i>
                )}
                Download Approval Letter
              </button>
            )}
            <button className="btn btn-light" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1"></i>
              {t("close")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="alert alert-light border">{t("no_details_available") || "No details available"}</div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <Row label={t("reference_number") || t("reference_no")} value={detail.ReferenceNumber} />
              <Row label={t("request_type")} value={requestTypeLabel} />
              <Row label={t("department")} value={detail?.Department || "-"} />
              <Row label={t("patient_name")} value={detail.PatientName} />
              <Row label={t("relation_to_employee")} value={detail.RelationToEmployee} />
              {(requestTypeRaw === "62" || requestTypeRaw === "63") && displayDisease ? (
                <Row label={t("disease")} value={displayDisease} />
              ) : null}
              <Row label={t("hospital_name")} value={displayHospitalName} />
              {requestTypeRaw === "62" && <Row label={t("hospital_address")} value={displayHospitalAddress} />}
              <Row label={t("request_creation_date")} value={createdDate} />
              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-muted fw-semibold">{t("request_status")}:</span>
                <span className={getStatusBadgeClass(detail.RequestStatus)}>
                  {getStatusLabel(detail.RequestStatus, t)}
                </span>
              </div>
              <Row label={t("ApproverUserID") || "Approver User ID"} value={detail?.ApproverUserID || "-"} />
              <Row label={t("last_latest_approver") || "Last / Latest Approver"} value={lastApprover} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}:</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default ViewMedicalAttendance;
